import { useCallback, useEffect, useRef } from 'react';

import {
  BRIDGE_ERROR_CODES,
  type IBridgeError,
  type IWebMessage,
  SYSTEM_ACTIONS,
  hostEvent,
  isSystemAction,
  parseWebMessage,
  rpcError,
  rpcResult
} from '../protocol';
import { type IIframeTransport, createIframeTransport } from '../transport';
import {
  type IBridgeHostDeps,
  handleSystemAction
} from './create-system-handlers';

/** Обработчик доменного (несистемного) действия сервиса. */
export type TLocalActionHandler = (
  message: IWebMessage,
  respond: (payload: unknown) => void,
  fail: (error: IBridgeError) => void
) => void;

export interface IUseIframeBridgeOptions {
  /** Ref на iframe-элемент сервиса. */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Origin сервиса — целевой для postMessage и allowlist входящих сообщений. */
  serviceOrigin: string;
  /** Зависимости хоста, к которым маппятся системные действия. */
  deps: IBridgeHostDeps;
  /** Сервис прислал `bridge.ready` — пора отправить `init`. */
  onReady?: () => void;
  /** Доменные действия сервиса (не системные). */
  onLocalAction?: TLocalActionHandler;
}

export interface IIframeBridgeApi {
  /** Отправить событие оболочка → сервис (с опциональной корреляцией для RPC-ответа). */
  sendEvent: (event: string, payload: unknown, correlationId?: string) => void;
}

/**
 * Хост моста на стороне оболочки: парсит входящие сообщения сервиса, маршрутизирует
 * системные действия на deps, доменные — в onLocalAction, и отвечает на RPC по `id`.
 * Транспорт — postMessage (cross-origin). Зеркало нативного useWebViewBridge.
 */
export function useIframeBridge(
  options: IUseIframeBridgeOptions
): IIframeBridgeApi {
  const { iframeRef, serviceOrigin } = options;

  // Свежие колбэки/deps держим в рефах, чтобы не пересоздавать транспорт на каждый рендер.
  // Обновляем в эффекте (не в рендере) — иначе сработает правило react-hooks/refs.
  const depsRef = useRef(options.deps);
  const onReadyRef = useRef(options.onReady);
  const onLocalActionRef = useRef(options.onLocalAction);

  useEffect(() => {
    depsRef.current = options.deps;
    onReadyRef.current = options.onReady;
    onLocalActionRef.current = options.onLocalAction;
  });

  const transportRef = useRef<IIframeTransport | null>(null);

  useEffect(() => {
    const transport = createIframeTransport({
      getTarget: () => iframeRef.current?.contentWindow ?? null,
      targetOrigin: serviceOrigin,
      allowedOrigins: serviceOrigin ? [serviceOrigin] : [],
      onMessage: (raw) => {
        const parsed = parseWebMessage(raw);
        if (!parsed.ok) return;

        const message = parsed.message;
        const { id } = message;

        if (message.action === SYSTEM_ACTIONS.Ready) {
          onReadyRef.current?.();
          return;
        }

        const respond = (resPayload: unknown) => {
          if (id) transport.post(rpcResult(id, resPayload));
        };
        const fail = (error: IBridgeError) => {
          if (id) transport.post(rpcError(id, error));
        };

        if (isSystemAction(message.action)) {
          handleSystemAction(message.action, message.payload, depsRef.current)
            .then(respond)
            .catch((err: unknown) => {
              fail({
                code: BRIDGE_ERROR_CODES.HANDLER_FAILED,
                message: err instanceof Error ? err.message : String(err)
              });
            });
          return;
        }

        if (onLocalActionRef.current) {
          onLocalActionRef.current(message, respond, fail);
        } else {
          fail({
            code: BRIDGE_ERROR_CODES.UNKNOWN_ACTION,
            message: message.action
          });
        }
      }
    });

    transportRef.current = transport;
    return () => {
      transport.destroy();
      transportRef.current = null;
    };
  }, [iframeRef, serviceOrigin]);

  const sendEvent = useCallback(
    (event: string, payload: unknown, correlationId?: string) => {
      transportRef.current?.post(hostEvent(event, payload, correlationId));
    },
    []
  );

  return { sendEvent };
}
